/**
 * EquipmentPopover — cursor-following popover shown on furniture/equipment hover in InfoMode.
 *
 * Equipment identifiers come from the href attribute of <use> elements in the SVG
 * (e.g. "defs.svg#dc" → id "dc") or from the CSS class of casework groups
 * (e.g. class="casework kitchenette" → id "kitchenette").
 *
 * Named furniture items use the id directly as an i18n key:  equipment.{id}
 * Wardrobe items use the pattern sz_{width}x{depth} — width/depth are extracted
 * and shown as dimensions under the shared i18n label equipment.sz.
 */

import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { parseEquipmentItem }   from '../../utils/popoverItems.js'

import { BottomSheet }      from '../../../../shared/ui/BottomSheet/BottomSheet.jsx'

import { HoverPopoverCard }   from './HoverPopoverCard.jsx'

import styles from './HoverPopoverCard.module.scss'

/**
 * @param {object}              props
 * @param {string|null}         props.equipmentId  — raw equipment id (e.g. 'dc', 'sz_60x178', 'kitchenette')
 * @param {{x:number,y:number}} props.position     — seed cursor position for initial placement
 * @param {boolean}             props.isOpen       — controls Popover visibility
 * @param {function}            [props.onClose]    — called by Popover on Escape / scroll
 * @param {boolean}             props.isMobile     — indicates if the device is mobile
 */
export function EquipmentPopover({ equipmentId, position, scale, isMobile, isOpen, onClose }) {
  const { t } = useI18n()

  const { name, dimensions } = parseEquipmentItem({id:equipmentId, scale, t})

  return (
    isMobile 
      ? <BottomSheet
          isOpen={isOpen}
          onClose={onClose}
        >
          <BottomSheet.Header 
            closeLabel={t('product.popover_close')}
          >
            <span className={styles.heading}>{name}</span>
          </BottomSheet.Header>
          <BottomSheet.Content>
          {dimensions && <div className={styles.dimensions}>{dimensions}</div>}
          </BottomSheet.Content>
        </BottomSheet>
      : <HoverPopoverCard
          isOpen={isOpen}
          position={position}
          onClose={onClose}
          header={name}
      >
        {dimensions && <div className={styles.dimensions}>{dimensions}</div>}
      </HoverPopoverCard>
  )
}
